/**
 * InterestedPage - Dedicated "Interested" Candidates Page
 * Route: /interested
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Heart, Bookmark, MapPin, Globe, Search, Building2, X, IdCard, Send, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from '../components/RateLimitNotification';
import {
  RecruiterInlineEmpty,
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import AtsPushButton from '../components/AtsPushButton';
import PassportViewer from '../components/Passport/PassportViewer';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface Candidate {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  cv_language?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  career_status?: string;
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
  explorer_id?: string;
  is_interested?: boolean;
}

const InterestedPage: React.FC = () => {
  const { t } = useTranslation('interestedFeed');
  const { t: tCard } = useTranslation('hiringBoard');
  const navigate = useNavigate();
  const { apiCall, user, authReady, isLoading: authLoading } = useAuth();

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [noCompany, setNoCompany] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [savedCvIds, setSavedCvIds] = useState<Set<string>>(new Set());
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [langFilter, setLangFilter] = useState<'all' | 'de' | 'en'>('all');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  useEffect(() => {
    if (!user || !authReady) return;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/interested-feed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: '', top_k: 20 }),
        });
        if (!resp.ok) {
          if (resp.status === 429) {
            const apiError = await handleApiError(resp);
            if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
          } else {
            setNoCompany(true);
          }
          setLoading(false);
          return;
        }
        const data = await resp.json();
        if (data.success) {
          setCandidates(data.candidates || []);
        } else {
          setNoCompany(true);
        }
      } catch {
        setNoCompany(true);
      }

      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
        if (resp.ok) {
          const data = await resp.json();
          setSavedCvIds(new Set((data.saved_cvs || []).map((s: { cv_id: string }) => s.cv_id)));
        }
      } catch { /* ignore */ }

      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.company?.id) setCompanyId(data.company.id);
        }
      } catch { /* ignore */ }

      setLoading(false);
    })();
  }, [user?.userId, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSave = async (cvId: string) => {
    const isSaved = savedCvIds.has(cvId);
    if (isSaved) {
      setSavedCvIds((prev) => { const next = new Set(prev); next.delete(cvId); return next; });
      apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs/${cvId}`, { method: 'DELETE' }).catch(() => {});
    } else {
      setSavedCvIds((prev) => new Set(prev).add(cvId));
      apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_id: cvId, prompt: '' }),
      }).catch(() => {});
    }
  };

  const filtered = langFilter === 'all'
    ? candidates
    : candidates.filter((c) => c.cv_language?.toLowerCase() === langFilter);

  if (authLoading) {
    return <RecruiterLoadingScreen icon={Heart} />;
  }

  if (!user) {
    return <RecruiterLoggedOutGate returnTo="/interested" />;
  }

  if (loading) {
    return <RecruiterLoadingScreen icon={Heart} />;
  }

  if (noCompany) {
    return (
      <RecruiterPageShell>
        <RecruiterInlineEmpty
          icon={Building2}
          title={t('empty.no_company_title')}
          description={t('empty.no_company_desc')}
          action={
            <Link
              to="/company/manage"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Building2 className="size-4" /> {t('empty.create_company')}
            </Link>
          }
        />
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell>
      {rateLimitInfo && (
        <div className="mb-6">
          <RateLimitNotification rateLimitInfo={rateLimitInfo} feature="candidate_search" onClose={() => setRateLimitInfo(null)} />
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            <span className="font-semibold tabular-nums text-slate-900">{filtered.length}</span>
            {' '}{t('tabs.interested')}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {(['all', 'de', 'en'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLangFilter(lang)}
                className={`rounded-lg px-2.5 py-1 text-[13px] transition-colors ${
                  langFilter === lang
                    ? 'bg-slate-900 font-medium text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {lang === 'all' ? t('filters.all_languages') : lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <RecruiterInlineEmpty
          icon={Heart}
          title={t('empty.interested_coming_title')}
          description={t('empty.interested_coming_desc')}
          action={
            <Link
              to="/candidate-search"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Search className="size-4" /> {t('empty.explore_candidates')}
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c) => {
            const contacted = !!c.reach_out_id;

            return (
              <article
                key={c.cv_id}
                className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-colors hover:border-slate-300"
              >
                <div className="flex items-center justify-end gap-3 border-b border-slate-100 px-4 py-2.5">
                  <div className="flex shrink-0 items-center gap-1.5">
                    {contacted && (
                      <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                        {t('card.already_contacted')}
                      </span>
                    )}
                    {c.cv_language && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                        {c.cv_language}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleSave(c.cv_id)}
                      className={`rounded-lg p-1 transition-colors ${
                        savedCvIds.has(c.cv_id) ? 'text-slate-900' : 'text-slate-300 hover:text-slate-500'
                      }`}
                      aria-label={t('card.save', { defaultValue: 'Save' })}
                    >
                      <Bookmark className={`size-3.5 ${savedCvIds.has(c.cv_id) ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <div className="min-w-0 flex-1">
                    {c.current_role && (
                      <h3 className="text-sm font-semibold leading-snug text-slate-900">{c.current_role}</h3>
                    )}
                    {c.current_level && (
                      <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500">
                        {c.current_level}
                      </span>
                    )}

                    <div className="mt-2 space-y-1 text-xs leading-snug text-slate-600">
                      {c.base_locations && (
                        <p className="flex min-w-0 items-start gap-1.5">
                          <MapPin className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                          <span className="min-w-0">
                            <span className="font-medium text-slate-400">{tCard('card.base_location')}</span>
                            {' · '}
                            {c.base_locations}
                          </span>
                        </p>
                      )}
                      {c.role_preferences && (
                        <p className="flex min-w-0 items-start gap-1.5">
                          <Heart className="mt-0.5 size-3 shrink-0 fill-pink-400 text-pink-400" aria-hidden />
                          <span className="min-w-0 line-clamp-2">
                            <span className="font-medium text-slate-400">{tCard('card.seeking')}</span>
                            {' · '}
                            {c.role_preferences}
                          </span>
                        </p>
                      )}
                      {c.preferred_locations && (
                        <p className="flex min-w-0 items-start gap-1.5">
                          <Globe className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                          <span className="min-w-0">
                            <span className="font-medium text-slate-400">{tCard('card.preferred_location')}</span>
                            {' · '}
                            {c.preferred_locations}
                          </span>
                        </p>
                      )}
                    </div>

                    {c.keywords?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.keywords.slice(0, 4).map((kw) => (
                          <span key={kw} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{kw}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-0.5 border-t border-slate-100 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setSelectedCandidate(c)}
                    className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-900"
                  >
                    <IdCard className="size-3.5" />
                    {t('detail.view_passport', { defaultValue: 'Passport' })}
                  </button>
                  {!c.reach_out_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/candidate-search?reach_out=${c.cv_id}`)}
                      className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-pink-600 transition-all hover:bg-pink-50"
                    >
                      <Send className="size-3.5" />
                      {t('detail.contact_candidate')}
                    </button>
                  )}
                  {c.reach_out_id && c.conversation_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/recruiter-inbox?conversation=${c.conversation_id}`)}
                      className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-emerald-600 transition-all hover:bg-emerald-50"
                    >
                      <MessageSquare className="size-3.5" />
                      {t('detail.go_to_chat')}
                    </button>
                  )}
                  {companyId && c.explorer_id && (
                    <AtsPushButton
                      companyId={companyId}
                      candidateUserId={c.explorer_id}
                      cvId={c.cv_id}
                      t={t as never}
                      appearance="outline"
                      hideIcons
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selectedCandidate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4"
            onClick={() => setSelectedCandidate(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="flex h-[90vh] max-w-[600px] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <span className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-slate-900">
                  <IdCard className="size-3.5" /> {t('detail.view_passport', { defaultValue: 'Passport' })}
                </span>
                <button type="button" onClick={() => setSelectedCandidate(null)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                  <X className="size-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <PassportViewer
                  cvId={selectedCandidate.cv_id}
                  audience="recruiter"
                  candidateId={selectedCandidate.explorer_id}
                  recruiterPrefs={{
                    career_status: selectedCandidate.career_status,
                    base_locations: selectedCandidate.base_locations,
                    preferred_locations: selectedCandidate.preferred_locations,
                    role_preferences: selectedCandidate.role_preferences,
                    current_role: selectedCandidate.current_role,
                    current_level: selectedCandidate.current_level,
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

export default InterestedPage;
